import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { LoadingSpinner } from '@/components/common';
// Only the error PARSERS are borrowed from the api module -- not its axios
// instance, which would attach a bearer token and run the refresh interceptor.
// A visitor here has no session and must never send one. Same decision as
// PublicQuote, BrokerProfile and InsurersDirectory.
import { NON_FIELD_KEY, getErrorMessage, getFieldErrors } from '@/services/api';
import { ManualVehiclePicker, type ManualVehicleChoice } from './ManualVehiclePicker';
import { DUPLICATE_FALLBACK, attemptKeyFor, isStartResponse, quotePagePath, type StartResponse } from './StartNotes';
import { quoteBasePath } from './PublicQuoteChooser';
import { ADDONS, NCB_SLABS } from '@/utils/addons';

/**
 * BOARD #266b -- the visitor-facing half of the public self-service quote.
 *
 * ROUTE: `/public/quote/:brokerSlug/motor` (the Motor tile of the broker's
 * Motor / Health / Travel chooser), registered in `App.tsx` OUTSIDE
 * `ProtectedRoute` and outside the dashboard layout -- e.g.
 * `https://base.policy.online/public/quote/acme-insurance/motor`. Also
 * slug-less at `/quote/motor` for a broker's CUSTOM DOMAIN, behind
 * `HostQuoteGate`. The platform's own site does not quote (owner, 24 Sep):
 * on the platform host without a slug the server resolves nobody.
 *
 * WHAT THIS PAGE IS
 * -----------------
 * Find the vehicle (straight from the registry -- Vahan -- on the first
 * search; the visitor picks Make -> Model -> Variant when the registry leaves
 * us without a variant, or when they go on without the registry), take the
 * visitor's details, ask the insurers for a real price, and then go to THE
 * SAME FINAL PAGE every line uses, `/public/quotes/:token` (`PublicQuote`),
 * handing it the start response to draw above the options. That page keeps
 * re-reading the quote so an advisor's hand-entered price appears in place;
 * choosing an option, KYC, declaring payment and downloading the policy are
 * its, driven by the server's `customer_state`.
 *
 * THREE ENDPOINTS, ALL OF WHICH ALREADY EXIST:
 *   GET  /policy/api/public/vehicles/lookup/<reg>/?site=<slug>&external=true
 *   POST /policy/api/public/quotes/start/                        (origination)
 *   GET  /policy/api/public/quotes/<public_token>/               (the options)
 *
 * THE SLUG, WHEN PRESENT, IS SENT ON EVERY CALL and wins on any host. It is not
 * a broker id and cannot be used as one: the server only ever turns it into a
 * publicly-live broker, and that broker's own insurer credentials price it.
 *
 * WHAT IT MUST NEVER DO. No premium appears on this page that the server did
 * not send. No "code sent" screen when no SMS channel exists. No success screen
 * that hides a dead end: every blocked outcome below says what is pending and
 * who will act on it.
 */

const API_ROOT = '/policy/api/public';

/** How long the quote start may take. It prices synchronously (the insurer is
 *  called through the relay inside the request): 10-20 s usually, but GoDigit
 *  allows 30 s a call plus retries. The proxy allows this endpoint 120 s and
 *  gunicorn 120 s, so the browser waits just under that. */
const START_TIMEOUT_MS = 115_000;

/** Did the start request possibly REACH the server and simply not come back?
 *  A timeout, a dropped connection, or a gateway giving up (502/503/504) all
 *  mean the quote may already exist -- so the form must not be offered again. */
function mayHaveReachedServer(err: unknown): boolean {
  const e = err as { code?: string; response?: { status?: number } } | null;
  if (!e) return false;
  const status = e.response?.status;
  if (status === undefined) return true; // timeout / network: no answer at all
  return status === 502 || status === 503 || status === 504;
}

/**
 * B6 -- State pre-fill from the PIN code, and State as a real dropdown, on
 * THIS page too. Reuses the same two endpoints every authenticated form's
 * `AddressFields`/`usePincodeLookup` calls
 * (GET /geography/states/lookup/, GET /geography/pincodes/search/?code=,
 * both now AllowAny for exactly this reason -- see apps/geography/views.py),
 * but NOT via `geographyService`/the shared hook: those go through the
 * authenticated axios instance (`@/services/api`), which is exactly what
 * this file's own rule at the top says a visitor here must never touch --
 * "must never send" a bearer token that might exist from an unrelated
 * logged-in session in the same browser. Plain `axios` (already imported
 * above, the same client the vehicle-lookup and quote-start calls use) hits
 * the full authenticated PATH but sends no Authorization header at all,
 * which the now-AllowAny actions accept from anyone.
 */
const GEOGRAPHY_API_ROOT = '/policy/api/v1/geography';

interface PublicStateOption {
  value: string;
  label: string;
}

interface PublicPincodeMatch {
  code: string;
  city_name?: string;
  state_name?: string;
}

/** The field whitelist `_public_vehicle_payload` returns. Anything identifying
 *  a person is absent there by design, so it is absent here. */
interface PublicVehicle {
  make_id: string | null;
  make_name: string;
  model_id: string | null;
  model_name: string;
  variant_id: string | null;
  variant_name: string;
  /** `label` is unique within the list and is what the dropdown must render.
   *  Two rows under one model can share a NAME (2,290 such groups in the
   *  master) while carrying different insurer codes, so a list built from
   *  `name` offers the same line twice for two different premiums. The server
   *  adds the specs, and an ordinal where even those are identical. Optional
   *  so a response from before that existed still renders. */
  variant_options: {
    id: string;
    name: string;
    score: number;
    detail?: string;
    label?: string;
    /** Against the registry (Vahan) fuel: true / false, or null when either
     *  side is unknown. A `false` is a different car and is never pre-selected. */
    fuel_matches?: boolean | null;
  }[];
  suggested_variant_id: string | null;
  /** Up to eight options ACROSS the best-fitting models, best first, each
   *  labelled with its model ("SCORPIO M HAWK · S9 2WD — Diesel · 2179 cc ·
   *  7 seats"). Newer servers only; the picker leads with these. */
  top_variant_options?: {
    id: string;
    name: string;
    model_id?: string;
    model_name?: string;
    label?: string;
    score: number;
    fuel_matches?: boolean | null;
  }[];
  /** high / medium / low / ambiguous / none. Newer servers only. */
  suggestion_confidence?: string;
  /** "remembered" when somebody already settled this registration's variant
   *  (never who, or how); "chosen"; or null. */
  variant_source?: string | null;
  model_options: { id: string; name: string; make_name: string }[];
  variant_hint: string;
  fuel_type: string;
  manufacturing_year: number | null;
  registration_date: string | null;
  rto_code: string;
  vehicle_category: string;
  engine_cc: number | null;
  seating_capacity: number | null;
}

interface LookupResponse {
  found: boolean;
  source?: string;
  is_external?: boolean;
  /** "nothing is chosen yet". NOT the question this page needs answered before
   *  drawing a picker: it goes false the moment a variant matches, including
   *  when it matches the WRONG one. Gating the picker on this flag alone is
   *  what made the correction unreachable -- every candidate was fetched,
   *  scored and then rendered nowhere. */
  needs_variant_choice?: boolean;
  /** "there is more than one answer here". This is what the picker is drawn
   *  from. */
  variant_choice_offered?: boolean;
  /** "...and we cannot tell the front-runners apart". Nothing may be
   *  pre-selected while this is true: the twins carry different insurer codes,
   *  so a pre-selection the visitor scrolled past is a coin toss on the
   *  premium. */
  variant_is_ambiguous?: boolean;
  external_available?: boolean;
  /** The registry found the vehicle but nothing in the catalogue matched it
   *  (no variant, no candidates). Sent by newer servers; derived below for
   *  older ones. The visitor then picks Make -> Model -> Variant. */
  needs_manual_vehicle?: boolean;
  vehicle?: PublicVehicle;
}

/** Found in the registry, but with nothing a quote can be priced on. */
function needsManualVehicle(data: LookupResponse): boolean {
  if (data.needs_manual_vehicle) return true;
  const v = data.vehicle;
  if (!v) return false;
  return (
    !v.variant_id &&
    !v.suggested_variant_id &&
    (v.variant_options ?? []).length === 0 &&
    (v.top_variant_options ?? []).length === 0
  );
}

/** The best matches across models first, then the rest of the matched model's
 *  variants -- every option once. Fuel-contradicting rows stay listed (the
 *  server marks them) but last, and are never pre-selected. */
function variantChoices(v: PublicVehicle) {
  const top = (v.top_variant_options ?? []).map((o) => ({ ...o, label: o.label ?? o.name }));
  const seen = new Set(top.map((o) => o.id));
  const rest = (v.variant_options ?? [])
    .filter((o) => !seen.has(o.id))
    .map((o) => ({ ...o, label: o.label ?? o.name }));
  return { top, rest };
}

/** Where the vehicle step got to. Every one of these renders something a person
 *  can act on -- there is no state here that is only a spinner. */
type LookupState =
  | { status: 'idle' }
  | { status: 'searching' }
  | { status: 'found'; data: LookupResponse }
  | { status: 'not_found'; externalAvailable: boolean }
  | { status: 'unavailable'; message: string };

type Stage = 'vehicle' | 'pick_vehicle' | 'details' | 'done' | 'uncertain' | 'already';

const FIELD_LABELS: Record<string, string> = {
  name: 'Your name',
  phone: 'Mobile number',
  email: 'Email',
  pincode: 'Pincode',
  registration_number: 'Registration number',
  has_active_tp_policy: 'My vehicle has a separate third-party policy that is still active',
  tp_insurer_name: 'Third-party policy insurer',
  tp_policy_number: 'Third-party policy number',
  tp_policy_start_date: 'Third-party policy started on',
  tp_policy_expiry: 'Third-party policy expires on',
  previous_insurer_name: 'Current insurance company',
  previous_policy_number: 'Current policy number',
  previous_policy_expiry: 'Current policy expires on',
  previous_ncb: 'No-claim bonus earned (%)',
  has_claim_in_previous: 'I made a claim on my current policy',
  dob: 'Date of birth',
  gender: 'Gender',
  address_line1: 'Address',
  city: 'City',
  state: 'State',
  idv: 'Insured declared value (IDV)',
  engine_number: 'Engine number',
  chassis_number: 'Chassis number',
};

/** The fields the details form actually draws an input for. A message keyed to
 *  anything else (`registration_number`, `variant_id`, `site`, ...) has nowhere
 *  to appear, and a form that silently does nothing when you press the button is
 *  this repo's signature bug wearing a different hat -- so those are folded into
 *  the banner instead (see the catch block in `submit`). */
const RENDERED_FIELDS = new Set([
  'name',
  'phone',
  'email',
  'pincode',
  // BOARD #278's optional groups. Each of these DOES draw an input below, so a
  // per-field message from the server has somewhere to land.
  'previous_insurer_name',
  'previous_policy_number',
  'previous_policy_expiry',
  'previous_ncb',
  'has_claim_in_previous',
  'has_active_tp_policy',
  'tp_insurer_name',
  'tp_policy_number',
  'tp_policy_start_date',
  'tp_policy_expiry',
  'dob',
  'gender',
  'address_line1',
  'city',
  'state',
  'idv',
  'engine_number',
  'chassis_number',
  // Ask-first mode's add-on checkboxes.
  'addons',
]);

function normaliseRegistration(value: string): string {
  return value.trim().toUpperCase().replace(/[\s-]/g, '');
}

/** Mirrors `core.security.validate_vehicle_registration`. Used ONLY to decide
 *  whether a half-typed number is worth carrying forward -- the server revalidates
 *  it, and this copy never decides anything the server does not decide again. */
function looksLikeRegistration(value: string): boolean {
  return /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/.test(value);
}

/**
 * Which variant the picker starts on.
 *
 * The server's own suggestion when it made one; otherwise the variant matching
 * applied, when the server does not call it ambiguous; otherwise -- ambiguous,
 * or nothing matched -- the HIGHEST-SCORING candidate, preferring the matched
 * one among equal leaders.
 *
 * Owner, 24 Sep: pre-select the best candidate "so pricing can proceed". This
 * reverses E4's "never guess": two same-named rows can carry different insurer
 * codes, so the page says, beside the picker, that it chose the closest match
 * and that the visitor should check it against their RC -- and "I am not
 * sure" stays in the list as an explicit choice.
 */
function initialVariant(data: LookupResponse): string | null {
  const v = data.vehicle;
  if (!v) return null;
  if (v.suggestion_confidence !== undefined) {
    // A server that grades its suggestion (25 Sep): pre-select ONLY what it is
    // sure of -- an applied/remembered variant nothing makes ambiguous, else
    // the suggestion at high or medium confidence. Anything less is asked.
    const graded = [...(v.top_variant_options ?? []), ...(v.variant_options ?? [])];
    const contradicted = (id: string | null | undefined) =>
      !!id && graded.some((o) => o.id === id && o.fuel_matches === false);
    if (v.variant_id && !data.variant_is_ambiguous && !contradicted(v.variant_id)) return v.variant_id;
    const sure = v.suggestion_confidence === 'high' || v.suggestion_confidence === 'medium';
    if (sure && !data.variant_is_ambiguous && v.suggested_variant_id && !contradicted(v.suggested_variant_id)) {
      return v.suggested_variant_id;
    }
    return null;
  }
  const options = v.variant_options ?? [];
  // NEVER a variant whose fuel contradicts the registry (PROD 24 Sep: a
  // PETROL/HYBRID Ciaz was pre-selected as the DIESEL "Smart Hybrid ZDI+").
  // The server already withholds those as the match/suggestion; this guards
  // the page against an older server too.
  const contradicts = (id: string | null | undefined) =>
    !!id && options.some((o) => o.id === id && o.fuel_matches === false);
  if (v.suggested_variant_id && !contradicts(v.suggested_variant_id)) return v.suggested_variant_id;
  if (!data.variant_is_ambiguous && v.variant_id && !contradicts(v.variant_id)) return v.variant_id;
  if (options.length === 0) return contradicts(v.variant_id) ? null : (v.variant_id ?? null);
  const eligible = options.filter((o) => o.fuel_matches !== false);
  // No candidate has the registry's fuel: ask, do not guess.
  if (eligible.length === 0) return null;
  const scoreOf = (o: { score: number }) => (Number.isFinite(Number(o.score)) ? Number(o.score) : 0);
  const top = Math.max(...eligible.map(scoreOf));
  const leaders = eligible.filter((o) => scoreOf(o) === top);
  return (leaders.find((o) => o.id === v.variant_id) ?? leaders[0]).id;
}

function Panel({
  tone,
  title,
  children,
}: {
  tone: 'neutral' | 'good' | 'warn';
  title: string;
  children: React.ReactNode;
}) {
  const tones = {
    neutral:
      'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    good: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-900 dark:text-green-200',
    warn: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200',
  } as const;
  return (
    <section className={`mb-6 rounded-xl border p-6 shadow-sm ${tones[tone]}`}>
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="mt-2 text-sm">{children}</div>
    </section>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-sm text-red-600 dark:text-red-400">{message}</p>;
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

export function PublicQuoteJourney() {
  const { brokerSlug } = useParams<{ brokerSlug?: string }>();

  const [stage, setStage] = useState<Stage>('vehicle');
  const [registration, setRegistration] = useState('');
  const [lookup, setLookup] = useState<LookupState>({ status: 'idle' });
  const [vehicle, setVehicle] = useState<PublicVehicle | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);
  /** The visitor's own Make -> Model -> Variant pick, when the registry left
   *  us without a variant (or they went on without the registry). */
  const [manualChoice, setManualChoice] = useState<ManualVehicleChoice | null>(null);
  /** "My vehicle is not in this list": the registry's record is kept (RTO,
   *  year, registration) and only the variant is picked by hand. */
  const [pickManually, setPickManually] = useState(false);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    pincode: '',
    // BOARD #278. All optional, and all of them were fields the QuoteRequest
    // already had columns for. The previous-policy group is the one that
    // matters most: without it `start_public_quote` recorded every visitor as
    // NEW BUSINESS, `build_previous_insurer` returned {} and the insurer priced
    // the car without the customer's no-claim bonus -- a premium too high,
    // presented to them as their price.
    previous_insurer_name: '',
    previous_policy_number: '',
    previous_policy_expiry: '',
    previous_ncb: '',
    has_claim_in_previous: false,
    // 24 Sep. Own-Damage only (SAOD) is priced ONLY when the visitor says they
    // hold a separate third-party policy that is still running -- the insurer
    // refuses SAOD without one.
    has_active_tp_policy: false,
    tp_insurer_name: '',
    tp_policy_number: '',
    tp_policy_start_date: '',
    tp_policy_expiry: '',
    dob: '',
    gender: '',
    address_line1: '',
    city: '',
    state: '',
    idv: '',
    engine_number: '',
    chassis_number: '',
    // Ask-first mode only: platform add-on codes (`utils/addons.ts`).
    addons: [] as string[],
  });
  // The broker's choice (`public_quote_customise`): "ask_first" asks claim /
  // NCB / add-ons / IDV on this form before the first price; "quote_first"
  // (the default, and what an unreachable answer means) prices straight away
  // and the quote page offers "Adjust your quote" afterwards.
  const [askFirst, setAskFirst] = useState(false);
  // Collapsed by default. A visitor on a phone should be able to submit four
  // fields and be done; these are for the one who has their old policy to hand.
  const [showPrevious, setShowPrevious] = useState(false);
  const [showAboutYou, setShowAboutYou] = useState(false);

  // B6: State dropdown options + a quiet PIN-lookup hint. `statesLoadFailed`
  // is the ONE case this page falls back to a plain text State box instead
  // of a dropdown -- the states list itself failing to load anonymously must
  // not trap the visitor with an empty, unusable <select> and no way to type
  // anything at all.
  const [stateOptions, setStateOptions] = useState<PublicStateOption[]>([]);
  const [statesLoadFailed, setStatesLoadFailed] = useState(false);
  const [pincodeHint, setPincodeHint] = useState<string | null>(null);
  const lastResolvedPincode = useRef<string | null>(null);

  // Fetched lazily, only once the visitor actually opens "A few more details"
  // (where State/City live) -- NOT at page mount. Stage 1 (registration
  // lookup) has its own hard rule that nothing is fetched until the visitor
  // asks for it (no registry search "on its own"); an unconditional
  // states-lookup call on every page load would spend exactly the kind of
  // request that rule exists to prevent, for a field most visitors never
  // reach.
  useEffect(() => {
    if (!showAboutYou || stateOptions.length > 0 || statesLoadFailed) return;
    let cancelled = false;
    axios
      .get<{ id: string; name: string; code: string }[]>(`${GEOGRAPHY_API_ROOT}/states/lookup/`)
      .then((res) => {
        if (cancelled) return;
        // This platform is India-only, but the seeded/live State table also
        // carries unrelated-country QA rows (see reviewer finding: prod's
        // "active" state list included two Brazil test rows). Every
        // authenticated form filters those out by matching the state's
        // `country` id against India's (see AddressFields' `useIndiaStates`)
        // -- but that needs `/geography/countries/lookup/`, which stays
        // authenticated-only per this batch's "don't expose anything else"
        // rule for anonymous visitors. Every real Indian state/UT code here
        // is exactly two letters (see seed_india.py); the junk rows are not,
        // so that shape is the anonymous-safe stand-in for a country filter.
        const list = Array.isArray(res.data) ? res.data : [];
        const indianOnly = list.filter((s) => /^[A-Za-z]{2}$/.test(s.code || ''));
        const options = indianOnly.length > 0 ? indianOnly : list;
        setStateOptions(options.map((s) => ({ value: s.name, label: s.name })));
      })
      .catch(() => {
        if (!cancelled) setStatesLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAboutYou]);

  // Deliberately NOT gated behind `showAboutYou` like the states-options
  // fetch above. Pincode is a REQUIRED, always-visible field here (motor is
  // zone-rated on it) -- blurring it is the visitor's own action on a field
  // they must fill in to submit at all, not an unconditional page-load
  // fetch for a section most visitors never open. This is exactly B6's core
  // requirement ("a 6-digit PIN pre-fills State ... across every address
  // form in scope, including the public journey"), so it runs on every
  // qualifying blur regardless of whether "A few more details" is open --
  // gating it on that unrelated section would mean the vast majority of
  // visitors, who never expand it, never get the auto-fill at all.
  const handlePincodeBlur = async (raw: string) => {
    const code = raw.trim();
    if (!/^\d{6}$/.test(code) || code === lastResolvedPincode.current) return;
    lastResolvedPincode.current = code;
    try {
      const res = await axios.get<{ results: PublicPincodeMatch[] }>(
        `${GEOGRAPHY_API_ROOT}/pincodes/search/?code=${encodeURIComponent(code)}`
      );
      const results = res.data.results;
      const exact = results.find((r) => r.code === code) || results[0];
      if (exact?.state_name) {
        setForm((prev) => ({
          ...prev,
          state: exact.state_name || prev.state,
          city: exact.city_name || prev.city,
        }));
        setPincodeHint(null);
        return;
      }
    } catch {
      // fall through to the quiet hint below
    }
    setPincodeHint('Could not auto-fill State/City for this PIN code — please enter them yourself in "A few more details" below.');
  };
  // The honeypot. A person never sees this input and never fills it; a
  // form-filling bot fills every input it finds. `company_website` is the name
  // the microsite lead form uses and the name the API's gate checks, so one
  // blocklist covers both.
  const [companyWebsite, setCompanyWebsite] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [result, setResult] = useState<StartResponse | null>(null);
  const [alreadyDetail, setAlreadyDetail] = useState('');

  const siteQuery = brokerSlug ? `?site=${encodeURIComponent(brokerSlug)}` : '';

  // Which mode this broker chose. One small read of the same resolver the
  // start endpoint uses, made only once the visitor reaches the details form
  // (where the answer matters) -- nothing is fetched on the vehicle step before
  // they ask. Any failure leaves the form exactly as it always was.
  const modeAsked = useRef(false);
  useEffect(() => {
    if (stage !== 'details' || modeAsked.current) return;
    modeAsked.current = true;
    Promise.resolve()
      .then(() => axios.get<{ quote_customise?: string }>(`${API_ROOT}/quotes/site/${siteQuery}`))
      .then((res) => setAskFirst(res?.data?.quote_customise === 'ask_first'))
      .catch(() => {
        /* quote_first */
      });
  }, [stage, siteQuery]);

  const normalisedRegistration = normaliseRegistration(registration);
  const carriedRegistration = looksLikeRegistration(normalisedRegistration)
    ? normalisedRegistration
    : '';

  const runLookup = async (external: boolean) => {
    const number = normaliseRegistration(registration);
    if (number.length < 4) {
      setLookup({
        status: 'unavailable',
        message: 'Enter the full registration number, for example MH12AB1234.',
      });
      return;
    }
    setLookup({ status: 'searching' });
    setManualChoice(null);
    setPickManually(false);

    const fetchLookup = (withRegistry: boolean) => {
      const params = new URLSearchParams();
      if (brokerSlug) params.set('site', brokerSlug);
      if (withRegistry) params.set('external', 'true');
      const qs = params.toString();
      return axios.get<LookupResponse>(
        `${API_ROOT}/vehicles/lookup/${encodeURIComponent(number)}/${qs ? `?${qs}` : ''}`
      );
    };

    const show = (data: LookupResponse) => {
      if (data.found && data.vehicle) {
        setVehicle(data.vehicle);
        setVariantId(initialVariant(data));
        setLookup({ status: 'found', data });
      } else {
        // `external_available` says a paid registry search is still POSSIBLE
        // (only ever true on a free-tier answer). It is not permission -- the
        // click is checked properly server-side.
        setLookup({
          status: 'not_found',
          externalAvailable: !!data.external_available,
        });
      }
    };

    try {
      show((await fetchLookup(external)).data);
    } catch (err) {
      // STRAIGHT TO THE REGISTRY, NEVER A DEAD END (owner, 24 Sep). The first
      // search now asks for the registry (Vahan) outright. The server still
      // answers from its own records first and only pays for a real vendor
      // call, under the broker's own plan: disabled, out of balance, or past
      // its public daily cap it refuses with 402 and a `found: false` body --
      // which IS the cached-only answer, so it is shown as such and the visitor
      // keeps "Continue without the registry".
      const refused = (err as { response?: { data?: LookupResponse } })?.response?.data;
      if (external && refused && refused.found === false) {
        setLookup({ status: 'not_found', externalAvailable: false });
        return;
      }
      if (external) {
        // Anything else (the vendor unreachable, a 5xx): ask again for our
        // own records only, so a registry outage cannot hide a car we hold.
        try {
          show((await fetchLookup(false)).data);
          return;
        } catch (retryErr) {
          setLookup({ status: 'unavailable', message: getErrorMessage(retryErr) });
          return;
        }
      }
      setLookup({ status: 'unavailable', message: getErrorMessage(err) });
    }
  };

  /** Go on WITHOUT an identified vehicle. The backend accepts this and says so:
   *  the quote and the lead are still written, pricing reports
   *  `not_attempted / vehicle_not_identified`, and the broker prices it by hand.
   *  A real path, not a fallback that loses the enquiry. */
  const goManual = () => {
    setVehicle(null);
    setVariantId(null);
    setManualChoice(null);
    // 24 Sep: not straight to the details form. Without a variant nothing can
    // be priced, so the visitor picks Make -> Model -> Variant first.
    setStage('pick_vehicle');
  };

  const choseVehicle = (choice: ManualVehicleChoice) => {
    setManualChoice(choice);
    setVariantId(choice.variantId);
    setStage('details');
  };

  /** Only reachable when the catalogue could not offer a choice. */
  const skipVehicle = () => {
    setManualChoice(null);
    setVariantId(null);
    setStage('details');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFieldErrors({});
    setFormError(null);

    const body: Record<string, unknown> = {
      // Resolved SERVER-SIDE into a publicly-live broker. Sent on every call
      // because the platform's own host identifies nobody.
      site: brokerSlug ?? '',
      company_website: companyWebsite,
      // Only THIS attempt may be handed back a quote it already started.
      attempt_key: attemptKeyFor(`motor:${brokerSlug ?? ''}`),
      name: form.name,
      phone: form.phone,
      email: form.email,
      pincode: form.pincode,
      // Every cover the insurer offers for this vehicle is now priced as its
      // own option, so the page no longer asks which one -- this is only the
      // request's headline cover.
      cover_type: 'comprehensive',
      // Carried even when the registry could not identify the vehicle -- it is
      // what the broker rings the visitor about, and dropping something they
      // typed is a quiet data loss. Sent only when it is SHAPED like a
      // registration, because a half-typed one would 400 against a field this
      // form does not draw.
      registration_number: carriedRegistration,
      // Explicit nulls, not empty strings: the serializer's UUID/date/integer
      // fields are `allow_null`, and `''` would be a validation error on each.
      variant_id: variantId || null,
      rto_code: vehicle?.rto_code || '',
      manufacturing_year: vehicle?.manufacturing_year ?? null,
      registration_date: vehicle?.registration_date ?? null,
      // BOARD #278. `business_type` is DERIVED by the server from whether a
      // previous policy was described, so it is deliberately not sent: this
      // form never asks the question in those words, and a page that guessed
      // would be a second opinion about the same fact.
      //
      // Nulls, not empty strings, for the date/number fields -- the serializer
      // declares them `allow_null` and `''` is a validation error on each.
      previous_insurer_name: form.previous_insurer_name.trim(),
      previous_policy_number: form.previous_policy_number.trim(),
      previous_policy_expiry: form.previous_policy_expiry || null,
      // A claim last year forfeits the bonus: nothing to send.
      previous_ncb:
        form.previous_ncb === '' || (askFirst && form.has_claim_in_previous) ? null : Number(form.previous_ncb),
      has_claim_in_previous: form.has_claim_in_previous,
      has_active_tp_policy: form.has_active_tp_policy,
      tp_insurer_name: form.has_active_tp_policy ? form.tp_insurer_name.trim() : '',
      tp_policy_number: form.has_active_tp_policy ? form.tp_policy_number.trim() : '',
      tp_policy_start_date: (form.has_active_tp_policy && form.tp_policy_start_date) || null,
      tp_policy_expiry: (form.has_active_tp_policy && form.tp_policy_expiry) || null,
      dob: form.dob || null,
      gender: form.gender,
      address_line1: form.address_line1.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      idv: form.idv === '' ? null : form.idv,
      engine_number: form.engine_number.trim(),
      chassis_number: form.chassis_number.trim(),
    };
    if (askFirst) body.addons = form.addons;

    try {
      // Pricing runs INSIDE this request (the insurer is asked through the
      // relay synchronously, ~10-20 s), so the wait is explicit: bare axios
      // has no timeout at all, and the page shows a "fetching live prices"
      // state meanwhile. nginx's 60 s proxy_read_timeout is the outer bound.
      const res = await axios.post<StartResponse>(`${API_ROOT}/quotes/start/${siteQuery}`, body, {
        timeout: START_TIMEOUT_MS,
      });
      if (isStartResponse(res.data)) {
        setResult(res.data);
        setStage('done');
      } else {
        setAlreadyDetail((res.data as { detail?: string })?.detail || DUPLICATE_FALLBACK);
        setStage('already');
      }
    } catch (err) {
      // NO RESUBMIT AFTER AN ANSWER THAT NEVER CAME. The server may have
      // written the lead and quote and still be pricing it; showing the form
      // again invited a second submission. (The server also replays a repeat
      // of the same phone + vehicle within minutes instead of duplicating it,
      // but the page must not rely on the visitor finding that out.)
      if (mayHaveReachedServer(err)) {
        setStage('uncertain');
        return;
      }
      // The API answers with per-field messages written for a customer, so they
      // are shown against the inputs. `getFieldErrors` already knows the
      // platform's envelope AND the bare `{"detail": ...}` the honeypot, the
      // per-IP gate (429) and the unknown-site case (404) return.
      const parsed = getFieldErrors(err);
      // Anything keyed to a field this form does not draw would be parsed and
      // then rendered NOWHERE, leaving a button that visibly does nothing. Those
      // are promoted to the banner so every refusal the server makes is visible.
      const orphaned = Object.entries(parsed)
        .filter(([key]) => key !== NON_FIELD_KEY && !RENDERED_FIELDS.has(key))
        .map(([, message]) => message);
      const nonField = parsed[NON_FIELD_KEY];
      setFieldErrors(parsed);
      const banner = [nonField, ...orphaned].filter(Boolean).join(' ');
      if (banner) {
        setFormError(banner);
      } else if (Object.keys(parsed).length === 0) {
        // 429 from the per-IP gate, 404 from an unknown site slug, and a network
        // drop all land here. `getErrorMessage` reads the server's own sentence.
        setFormError(getErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ----------------------------------------------------------- uncertain ---
  // The start request got no answer. It may have been saved and priced, so
  // there is no form here and nothing is re-posted.
  // ------------------------------------------------------------- already ---
  // A repeat of a request we already hold, from something other than the
  // attempt that made it: the server says so and hands over nothing that
  // opens it (pre-deploy review, 24 Sep).
  if (stage === 'already') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <Panel tone="neutral" title="We already have your request">
            <p role="status">{alreadyDetail}</p>
          </Panel>
        </div>
      </div>
    );
  }

  if (stage === 'uncertain') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <Panel tone="warn" title="Your request may already be with us">
            <p role="status">
              We did not hear back in time, but your request may already have reached us.
              Please do not submit it again -- your advisor will contact you on{' '}
              <span className="font-semibold">{form.phone}</span>.
            </p>
          </Panel>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- done ---
  // THE SAME FINAL PAGE FOR EVERY LINE (owner, 24 Sep): motor, health and
  // travel all land on `/public/quotes/<token>`, which draws what the start
  // said (`StartNotes`, from router state) above the options and keeps
  // re-reading the quote so the advisor's hand-entered prices appear in place.
  // The token is what the SERVER minted; the path is rebuilt from it rather
  // than following `quote_url` blindly.
  if (stage === 'done' && result) {
    return <Navigate to={quotePagePath(result.public_token)} replace state={{ start: result }} />;
  }

  // ------------------------------------------------------------ pricing ---
  // The start request prices synchronously -- the insurer is asked while the
  // visitor waits -- so the wait gets a screen of its own rather than a button
  // that looks stuck. Everything typed is kept in state; a refusal brings the
  // form straight back with its messages.
  if (stage === 'details' && submitting) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <section
            role="status"
            aria-live="polite"
            className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center shadow-sm"
          >
            <div className="flex justify-center">
              <LoadingSpinner />
            </div>
            <h1 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
              Fetching live prices...
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              We are asking the insurers for your premium now. This usually takes 10 to 20
              seconds -- please keep this page open.
            </p>
          </section>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------- details ---
  if (stage === 'details') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <header className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
              Step 2 of 2
            </p>
            <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
              Where should we send your quote?
            </h1>
          </header>

          <Panel tone="neutral" title="Your vehicle">
            {manualChoice ? (
              <p>
                {[manualChoice.makeName, manualChoice.modelName, manualChoice.variantName].join(' ')}
                {vehicle?.manufacturing_year ? ` (${vehicle.manufacturing_year})` : ''}
              </p>
            ) : vehicle ? (
              <p>
                {[vehicle.make_name, vehicle.model_name, vehicle.variant_name]
                  .filter(Boolean)
                  .join(' ')}
                {vehicle.manufacturing_year ? ` (${vehicle.manufacturing_year})` : ''}
              </p>
            ) : (
              <p>
                We could not identify the vehicle from the registry, so your advisor will
                confirm it with you and price the quote by hand.
              </p>
            )}
            {/* Shown so the visitor can see the number is being carried rather
                than quietly dropped when the registry could not match it. */}
            {carriedRegistration && (
              <p className="mt-1 font-mono text-xs">{carriedRegistration}</p>
            )}
            <button
              type="button"
              onClick={() => setStage('vehicle')}
              className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              Change vehicle
            </button>
          </Panel>

          <form onSubmit={submit} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm">
            {formError && (
              <div
                role="alert"
                className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-800 dark:text-red-300"
              >
                {formError}
              </div>
            )}

            {/* Off-screen rather than display:none -- some bots skip hidden
                inputs. aria-hidden and tabIndex keep it away from real people
                and from screen readers. */}
            <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
              <label htmlFor="company_website">Company website</label>
              <input
                id="company_website"
                name="company_website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={companyWebsite}
                onChange={(e) => setCompanyWebsite(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {FIELD_LABELS.name}
                </label>
                <input
                  id="name"
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
                <FieldError message={fieldErrors.name} />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {FIELD_LABELS.phone}
                </label>
                <input
                  id="phone"
                  inputMode="numeric"
                  className={inputClass}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required
                />
                <FieldError message={fieldErrors.phone} />
              </div>

              <div>
                {/* Required, and not out of tidiness: motor is zone-rated on the
                    pincode and the GoDigit payload builder refuses without one,
                    so a quote taken without it cannot be priced at all. */}
                <label htmlFor="pincode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {FIELD_LABELS.pincode}
                </label>
                <input
                  id="pincode"
                  inputMode="numeric"
                  className={inputClass}
                  value={form.pincode}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setForm({ ...form, pincode: digits });
                    if (pincodeHint) setPincodeHint(null);
                  }}
                  onBlur={(e) => {
                    void handlePincodeBlur(e.target.value);
                  }}
                  required
                />
                <FieldError message={fieldErrors.pincode} />
                {pincodeHint && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{pincodeHint}</p>
                )}
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {FIELD_LABELS.email} <span className="text-gray-500">(optional)</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className={inputClass}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                <FieldError message={fieldErrors.email} />
              </div>

              <p className="sm:col-span-2 text-sm text-gray-600 dark:text-gray-400">
                We will show you every type of cover we can price online for this vehicle --
                Comprehensive, Third-Party only and, if you have a separate third-party policy,
                Own-Damage only -- each with its own premium.
              </p>
            </div>

            {/* Ask-first (the broker's `public_quote_customise`): the choices
                that change the price, asked before the first one. Bound to
                the same form state the collapsed groups below use, whose
                duplicate inputs are not drawn in this mode. */}
            {askFirst && (
              <fieldset
                aria-label="Customise your quote"
                className="mt-6 rounded-lg border border-primary-200 bg-primary-50/40 p-4 dark:border-primary-800 dark:bg-primary-900/10"
              >
                <legend className="px-1 text-sm font-semibold text-gray-900 dark:text-white">
                  Customise your quote
                </legend>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Did you make a claim last year?
                    </legend>
                    <div className="mt-2 flex gap-4 text-sm text-gray-700 dark:text-gray-300">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="ask-claim"
                          checked={form.has_claim_in_previous}
                          onChange={() => setForm({ ...form, has_claim_in_previous: true })}
                        />
                        Yes
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="ask-claim"
                          checked={!form.has_claim_in_previous}
                          onChange={() => setForm({ ...form, has_claim_in_previous: false })}
                        />
                        No
                      </label>
                    </div>
                    <FieldError message={fieldErrors.has_claim_in_previous} />
                  </fieldset>
                  {!form.has_claim_in_previous && (
                    <div>
                      <label htmlFor="ask_previous_ncb" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Previous no-claim bonus
                      </label>
                      <select
                        id="ask_previous_ncb"
                        className={inputClass}
                        value={form.previous_ncb}
                        onChange={(e) => setForm({ ...form, previous_ncb: e.target.value })}
                      >
                        <option value="">Not sure</option>
                        {NCB_SLABS.map((slab) => (
                          <option key={slab} value={String(slab)}>
                            {slab}%
                          </option>
                        ))}
                      </select>
                      <FieldError message={fieldErrors.previous_ncb} />
                    </div>
                  )}
                  <div>
                    <label htmlFor="ask_idv" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      IDV you want (optional), ₹
                    </label>
                    <input
                      id="ask_idv"
                      inputMode="numeric"
                      className={inputClass}
                      value={form.idv}
                      onChange={(e) => setForm({ ...form, idv: e.target.value })}
                      aria-describedby="ask_idv_hint"
                    />
                    <p id="ask_idv_hint" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Leave blank for each insurer&rsquo;s own value. Each insurer keeps it within its range.
                    </p>
                    <FieldError message={fieldErrors.idv} />
                  </div>
                </div>
                <fieldset className="mt-4">
                  <legend className="text-sm font-medium text-gray-700 dark:text-gray-300">Add-ons</legend>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Not every insurer offers every add-on -- your quote shows what each one includes.
                  </p>
                  <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ADDONS.map((addon) => (
                      <label key={addon.code} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={form.addons.includes(addon.code)}
                          onChange={() =>
                            setForm({
                              ...form,
                              addons: form.addons.includes(addon.code)
                                ? form.addons.filter((c) => c !== addon.code)
                                : [...form.addons, addon.code],
                            })
                          }
                        />
                        <span>{addon.label}</span>
                      </label>
                    ))}
                  </div>
                  <FieldError message={fieldErrors.addons} />
                </fieldset>
              </fieldset>
            )}

            {/* BOARD #278. Two collapsed groups, both entirely optional.
                Collapsed because a visitor on a phone should be able to submit
                four fields and be done; present because the answers below are
                the difference between a premium that includes the customer's
                no-claim bonus and one that does not. */}
            <details
              className="mt-6 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              open={showPrevious}
              onToggle={(e) => setShowPrevious((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-white">
                Do you already have a policy on this vehicle?
              </summary>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Optional, but it usually makes the premium lower: your no-claim bonus can only be
                applied if the insurer knows about your last policy.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="previous_insurer_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.previous_insurer_name}
                  </label>
                  <input
                    id="previous_insurer_name"
                    className={inputClass}
                    value={form.previous_insurer_name}
                    onChange={(e) => setForm({ ...form, previous_insurer_name: e.target.value })}
                  />
                  <FieldError message={fieldErrors.previous_insurer_name} />
                </div>
                <div>
                  <label htmlFor="previous_policy_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.previous_policy_number}
                  </label>
                  <input
                    id="previous_policy_number"
                    className={inputClass}
                    value={form.previous_policy_number}
                    onChange={(e) => setForm({ ...form, previous_policy_number: e.target.value })}
                  />
                  <FieldError message={fieldErrors.previous_policy_number} />
                </div>
                <div>
                  <label htmlFor="previous_policy_expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.previous_policy_expiry}
                  </label>
                  <input
                    id="previous_policy_expiry"
                    type="date"
                    className={inputClass}
                    value={form.previous_policy_expiry}
                    onChange={(e) => setForm({ ...form, previous_policy_expiry: e.target.value })}
                  />
                  <FieldError message={fieldErrors.previous_policy_expiry} />
                </div>
                {!askFirst && (
                <div>
                  <label htmlFor="previous_ncb" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.previous_ncb}
                  </label>
                  <input
                    id="previous_ncb"
                    type="number"
                    min={0}
                    max={50}
                    className={inputClass}
                    value={form.previous_ncb}
                    onChange={(e) => setForm({ ...form, previous_ncb: e.target.value })}
                  />
                  <FieldError message={fieldErrors.previous_ncb} />
                </div>
                )}
                {!askFirst && (
                <div className="sm:col-span-2">
                  <label htmlFor="has_claim_in_previous" className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      id="has_claim_in_previous"
                      type="checkbox"
                      checked={form.has_claim_in_previous}
                      onChange={(e) => setForm({ ...form, has_claim_in_previous: e.target.checked })}
                    />
                    {FIELD_LABELS.has_claim_in_previous}
                  </label>
                  <FieldError message={fieldErrors.has_claim_in_previous} />
                </div>
                )}
                <div className="sm:col-span-2">
                  <label htmlFor="has_active_tp_policy" className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      id="has_active_tp_policy"
                      type="checkbox"
                      checked={form.has_active_tp_policy}
                      onChange={(e) => setForm({ ...form, has_active_tp_policy: e.target.checked })}
                    />
                    {FIELD_LABELS.has_active_tp_policy}
                  </label>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Needed for an Own-Damage only price. Many vehicles bought new carry a
                    long-term third-party policy that runs on after the own-damage cover ends.
                  </p>
                  <FieldError message={fieldErrors.has_active_tp_policy} />
                </div>
                {form.has_active_tp_policy && (
                  <>
                    <div>
                      <label htmlFor="tp_insurer_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {FIELD_LABELS.tp_insurer_name}
                      </label>
                      <input
                        id="tp_insurer_name"
                        className={inputClass}
                        value={form.tp_insurer_name}
                        onChange={(e) => setForm({ ...form, tp_insurer_name: e.target.value })}
                      />
                      <FieldError message={fieldErrors.tp_insurer_name} />
                    </div>
                    <div>
                      <label htmlFor="tp_policy_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {FIELD_LABELS.tp_policy_number}
                      </label>
                      <input
                        id="tp_policy_number"
                        className={inputClass}
                        value={form.tp_policy_number}
                        onChange={(e) => setForm({ ...form, tp_policy_number: e.target.value })}
                      />
                      <FieldError message={fieldErrors.tp_policy_number} />
                    </div>
                    <div>
                      <label htmlFor="tp_policy_start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {FIELD_LABELS.tp_policy_start_date} <span className="text-gray-500">(optional)</span>
                      </label>
                      <input
                        id="tp_policy_start_date"
                        type="date"
                        className={inputClass}
                        value={form.tp_policy_start_date}
                        onChange={(e) => setForm({ ...form, tp_policy_start_date: e.target.value })}
                      />
                      <FieldError message={fieldErrors.tp_policy_start_date} />
                    </div>
                    <div>
                      <label htmlFor="tp_policy_expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {FIELD_LABELS.tp_policy_expiry}
                      </label>
                      <input
                        id="tp_policy_expiry"
                        type="date"
                        className={inputClass}
                        value={form.tp_policy_expiry}
                        onChange={(e) => setForm({ ...form, tp_policy_expiry: e.target.value })}
                      />
                      <FieldError message={fieldErrors.tp_policy_expiry} />
                    </div>
                  </>
                )}
              </div>
            </details>

            <details
              className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              open={showAboutYou}
              onToggle={(e) => setShowAboutYou((e.target as HTMLDetailsElement).open)}
            >
              <summary className="cursor-pointer text-sm font-medium text-gray-900 dark:text-white">
                A few more details (optional)
              </summary>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Filling these in now saves the broker asking for them later, and lets the insurer
                price against the value you want covered.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="dob" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.dob}
                  </label>
                  <input
                    id="dob"
                    type="date"
                    className={inputClass}
                    value={form.dob}
                    onChange={(e) => setForm({ ...form, dob: e.target.value })}
                  />
                  <FieldError message={fieldErrors.dob} />
                </div>
                <div>
                  <label htmlFor="gender" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.gender}
                  </label>
                  <select
                    id="gender"
                    className={inputClass}
                    value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value })}
                  >
                    <option value="">Prefer not to say</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                  <FieldError message={fieldErrors.gender} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.address_line1}
                  </label>
                  <input
                    id="address_line1"
                    className={inputClass}
                    value={form.address_line1}
                    onChange={(e) => setForm({ ...form, address_line1: e.target.value })}
                  />
                  <FieldError message={fieldErrors.address_line1} />
                </div>
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.city}
                  </label>
                  <input
                    id="city"
                    className={inputClass}
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                  />
                  <FieldError message={fieldErrors.city} />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.state}
                  </label>
                  {/* B6: a dropdown of every seeded state, pre-filled from
                      the PIN above. Falls back to a plain text box ONLY if
                      the states list itself could not be loaded -- an empty,
                      unusable <select> would otherwise trap a visitor who
                      still needs to answer this. */}
                  {statesLoadFailed ? (
                    <input
                      id="state"
                      className={inputClass}
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    />
                  ) : (
                    <select
                      id="state"
                      className={inputClass}
                      value={form.state}
                      onChange={(e) => setForm({ ...form, state: e.target.value })}
                    >
                      <option value="">
                        {stateOptions.length === 0 ? 'Loading states…' : 'Select state'}
                      </option>
                      {form.state && !stateOptions.some((o) => o.value === form.state) && (
                        <option value={form.state}>{form.state}</option>
                      )}
                      {stateOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  )}
                  <FieldError message={fieldErrors.state} />
                </div>
                {!askFirst && (
                <div>
                  <label htmlFor="idv" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.idv}
                  </label>
                  <input
                    id="idv"
                    inputMode="decimal"
                    className={inputClass}
                    value={form.idv}
                    onChange={(e) => setForm({ ...form, idv: e.target.value })}
                  />
                  <FieldError message={fieldErrors.idv} />
                </div>
                )}
                <div>
                  <label htmlFor="engine_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.engine_number}
                  </label>
                  <input
                    id="engine_number"
                    className={inputClass}
                    value={form.engine_number}
                    onChange={(e) => setForm({ ...form, engine_number: e.target.value })}
                  />
                  <FieldError message={fieldErrors.engine_number} />
                </div>
                <div>
                  <label htmlFor="chassis_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FIELD_LABELS.chassis_number}
                  </label>
                  <input
                    id="chassis_number"
                    className={inputClass}
                    value={form.chassis_number}
                    onChange={(e) => setForm({ ...form, chassis_number: e.target.value })}
                  />
                  <FieldError message={fieldErrors.chassis_number} />
                </div>
              </div>
            </details>

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
            >
              {submitting ? 'Getting your quote...' : 'Get my quote'}
            </button>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Your details go to the broker whose website you are on, so they can send you
              a quote and confirm it with you.
            </p>
          </form>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------- pick vehicle ---
  if (stage === 'pick_vehicle') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <header className="mb-8">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
              Step 1 of 2
            </p>
            <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">Choose your vehicle</h1>
          </header>
          <Panel tone="neutral" title="Your vehicle">
            <ManualVehiclePicker onConfirm={choseVehicle} onSkip={skipVehicle} />
          </Panel>
          <p className="text-center text-sm">
            <button
              type="button"
              onClick={() => setStage('vehicle')}
              className="font-medium text-primary-600 hover:text-primary-700"
            >
              Look it up by registration number instead
            </button>
          </p>
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------- vehicle ---
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-10 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <Link
            to={quoteBasePath(brokerSlug)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            &larr; Motor, Health or Travel
          </Link>
          <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-primary-600 dark:text-primary-400">
            Step 1 of 2
          </p>
          <h1 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
            Get a motor insurance quote
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Start with your registration number and we will look the vehicle up.
          </p>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Straight to the registry (Vahan): see the catch in `runLookup`
            // for what happens when the broker's plan refuses the search.
            void runLookup(true);
          }}
          className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-sm"
        >
          <label htmlFor="registration_number" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {FIELD_LABELS.registration_number}
          </label>
          <input
            id="registration_number"
            className={inputClass}
            placeholder="MH12AB1234"
            value={registration}
            onChange={(e) => setRegistration(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            disabled={lookup.status === 'searching'}
            className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {lookup.status === 'searching' ? 'Looking up...' : 'Find my vehicle'}
          </button>
        </form>

        {lookup.status === 'searching' && (
          <div className="mb-6 flex justify-center">
            <LoadingSpinner />
          </div>
        )}

        {lookup.status === 'found' && vehicle && (
          <Panel tone="good" title="We found your vehicle">
            <p className="text-base font-medium">
              {[
                vehicle.make_name,
                // A best match from another model of the family (SCORPIO vs
                // SCORPIO M HAWK) names the car the visitor actually chose.
                (vehicle.top_variant_options ?? []).find((o) => o.id === variantId)?.model_name ??
                  vehicle.model_name,
              ]
                .filter(Boolean)
                .join(' ')}
              {vehicle.manufacturing_year ? ` (${vehicle.manufacturing_year})` : ''}
            </p>
            <p className="mt-1">
              {[vehicle.fuel_type, vehicle.rto_code].filter(Boolean).join(' · ')}
            </p>

            {/* The registry's match on a variant is a guess with a score, so the
                visitor gets to correct it for their own enquiry. What they pick
                is NOT taught to the shared mapping -- only a broker or POSP
                confirming does that, because a mapping moves premiums for
                everyone and an anonymous caller is accountable to nobody. */}
            {/* REACHABLE WHENEVER THERE IS A CHOICE, not only when nothing
                matched. `variant_choice_offered` is the server's "there is
                more than one answer here"; `needs_variant_choice` is kept
                beside it so a lone candidate that nothing has been chosen from
                is still selectable, and so a server that predates the new flag
                behaves as it always did. */}
            {needsManualVehicle(lookup.data) || pickManually ? (
              <div className="mt-4">
                <p className="mb-2 font-medium">
                  {pickManually
                    ? 'Choose your vehicle below.'
                    : 'We could not match this vehicle to an exact variant, so please choose it below.'}
                </p>
                <ManualVehiclePicker
                  initialMakeId={vehicle.make_id}
                  initialMakeName={vehicle.make_name}
                  fuel={vehicle.fuel_type}
                  onConfirm={choseVehicle}
                  onSkip={skipVehicle}
                />
              </div>
            ) : (
              <>
            {vehicle.variant_source === 'remembered' && !!vehicle.variant_id && (
              // Deliberately nothing more: who settled it, and how, is about
              // somebody's dealings with this car.
              <p className="mt-2 text-xs" data-testid="vehicle-recognised">
                We recognised this vehicle.
              </p>
            )}
            {(vehicle.variant_options.length > 0 || (vehicle.top_variant_options ?? []).length > 0) &&
              (lookup.data.variant_choice_offered ||
                lookup.data.needs_variant_choice ||
                (vehicle.top_variant_options ?? []).length > 1) && (
                <div className="mt-4">
                  <label htmlFor="variant" className="block text-sm font-medium">
                    Which variant is it?
                  </label>
                  <select
                    id="variant"
                    className={inputClass}
                    value={variantId ?? ''}
                    onChange={(e) => setVariantId(e.target.value || null)}
                  >
                    {/* Choosing this sends no variant, and the server then
                        reports `vehicle_not_identified` and prices nothing -- so
                        the label says that rather than letting it look free. */}
                    <option value="">I am not sure - my advisor will confirm</option>
                    {(() => {
                      // `label` is the server's disambiguated line and is
                      // unique within its list; `name` is the fallback for a
                      // response that predates it. The cross-model best
                      // matches lead (their labels name the model), then the
                      // matched model's full list, so nothing is unreachable.
                      const { top, rest } = variantChoices(vehicle);
                      const render = (o: { id: string; label: string }) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      );
                      if (top.length === 0) return rest.map(render);
                      return (
                        <>
                          <optgroup label="Best matches">{top.map(render)}</optgroup>
                          {rest.length > 0 && (
                            <optgroup label={`All ${vehicle.model_name} variants`}>
                              {rest.map(render)}
                            </optgroup>
                          )}
                        </>
                      );
                    })()}
                  </select>
                  {!variantId && (
                    <p className="mt-2 text-xs font-medium">
                      We could not tell which variant this is from the registry, so we have not
                      chosen for you. Pick the one on your registration certificate, or leave it
                      unset and your advisor will confirm it.
                    </p>
                  )}
                  {lookup.data.variant_is_ambiguous && !!variantId && (
                    // Said plainly beside the pre-filled field. The catalogue
                    // holds more than one entry that matches these words
                    // equally well, and they can price differently.
                    <p className="mt-2 text-xs font-medium">
                      More than one variant matches this vehicle, so we have picked the closest
                      match to get you a price. Please check it against your registration
                      certificate -- the variant can change the premium. Choose &quot;I am not
                      sure&quot; if you would rather your advisor confirm it.
                    </p>
                  )}
                  {vehicle.variant_hint && <p className="mt-1 text-xs">{vehicle.variant_hint}</p>}
                  <button
                    type="button"
                    onClick={() => {
                      setVariantId(null);
                      setPickManually(true);
                    }}
                    className="mt-2 text-xs font-medium text-primary-700 underline dark:text-primary-300"
                  >
                    My vehicle is not in this list
                  </button>
                </div>
              )}

            <button
              type="button"
              onClick={() => setStage('details')}
              className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700"
            >
              This is my vehicle - continue
            </button>
              </>
            )}
          </Panel>
        )}

        {lookup.status === 'not_found' && (
          <Panel tone="warn" title="We could not find that vehicle">
            {lookup.externalAvailable ? (
              <>
                <p>
                  It is not in our records. We can search the vehicle registry for it. Your
                  broker pays for that search, so it happens only when you ask -- never on
                  its own.
                </p>
                <button
                  type="button"
                  onClick={() => void runLookup(true)}
                  className="mt-4 w-full rounded-lg bg-primary-600 px-4 py-3 font-semibold text-white hover:bg-primary-700"
                >
                  Search the vehicle registry
                </button>
              </>
            ) : (
              <p>
                The registry lookup is not available right now, so we could not confirm the
                vehicle. You can still get a quote -- your advisor will confirm the vehicle
                with you.
              </p>
            )}
            <button
              type="button"
              onClick={goManual}
              className="mt-3 w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Continue without the registry
            </button>
          </Panel>
        )}

        {lookup.status === 'unavailable' && (
          <Panel tone="warn" title="We could not look that up right now">
            <p>{lookup.message}</p>
            <button
              type="button"
              onClick={goManual}
              className="mt-4 w-full rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-3 font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Continue without the registry
            </button>
          </Panel>
        )}

        {lookup.status === 'idle' && (
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Do not have the number to hand?{' '}
            <button
              type="button"
              onClick={goManual}
              className="font-medium text-primary-600 hover:text-primary-700"
            >
              Continue without it
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
